import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Box, Lock, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAdminAuth } from "../context/AdminAuthContext";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await login(email, password);
      
      // Check if password change is required
      if (response.user?.must_change_password) {
        navigate("/admin/change-password");
      } else {
        navigate("/admin/dashboard");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Invalid credentials";
      setError(typeof errorMsg === "string" ? errorMsg : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4 overflow-x-hidden" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
            <Box className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              ParamSnap
            </span>
          </div>
          <h1 className="text-lg sm:text-xl font-semibold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
            Admin Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-zinc-400 mt-2">
            Sign in to manage users and analytics
          </p>
        </div>

        <div className="bento-card p-5 sm:p-8">
          <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs sm:text-sm text-zinc-300">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@paramsnap.com"
                required
                className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs sm:text-sm text-zinc-300">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1 bg-[#0A0A0B] border-white/10 text-white placeholder:text-zinc-600 focus:border-blue-500 text-sm sm:text-base h-10 sm:h-11"
                data-testid="admin-password-input"
              />
            </div>

            {error && (
              <p className="text-xs sm:text-sm text-red-400 text-center">{error}</p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-2.5 sm:py-3 rounded-lg font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
                  Sign In
                </>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-[10px] sm:text-xs text-zinc-500 mt-5 sm:mt-6">
          paramsnap.com · Secure admin area
        </p>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
